import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Share2 } from 'lucide-react';

const articlesContent = {
  'introduction-to-stocks-protocol': {
    title: 'Introduction to Stocks Protocol L1',
    duration: '8 min',
    date: 'February 9, 2025',
    content: `
      <h2>What is Stocks Protocol?</h2>
      <p>Stocks Protocol is a revolutionary Layer 1 blockchain specifically designed for tokenized securities. Unlike general-purpose blockchains, our protocol is purpose-built to handle the unique requirements of stock and ETF trading—speed, security, regulatory compliance, and seamless settlement.</p>
      
      <h2>The Problem We Solve</h2>
      <p>Traditional stock markets are plagued by inefficiencies:</p>
      <ul>
        <li><strong>T+2 Settlement:</strong> When you buy a stock, it takes 2 business days to actually own it</li>
        <li><strong>Limited Hours:</strong> US markets only open 6.5 hours per day, 5 days a week</li>
        <li><strong>High Fees:</strong> Brokers, clearing houses, and custodians all take their cut</li>
        <li><strong>Geographic Restrictions:</strong> Many international investors can't access US equities easily</li>
      </ul>
      
      <h2>Our Solution</h2>
      <p>Stocks Protocol tokenizes real US stocks and ETFs on a dual-chain architecture using Stellar and XRPL. Each token is backed 1:1 by actual shares held in regulated custody.</p>
      
      <h3>Key Benefits:</h3>
      <ul>
        <li><strong>3-5 Second Settlement:</strong> True instant finality, not T+2</li>
        <li><strong>24/7 Trading:</strong> Markets never close on blockchain</li>
        <li><strong>Near-Zero Fees:</strong> Less than $0.01 per transaction</li>
        <li><strong>Global Access:</strong> Available in 150+ countries</li>
        <li><strong>Self-Custody:</strong> Your keys, your stocks</li>
      </ul>
      
      <h2>How It Works</h2>
      <p>When you buy sTSLA (tokenized Tesla) on Stocks Protocol:</p>
      <ol>
        <li>Your order is matched on our decentralized order book</li>
        <li>The trade settles in 3-5 seconds on-chain</li>
        <li>You receive sTSLA tokens in your wallet</li>
        <li>Each sTSLA is backed by 1 real TSLA share in custody</li>
      </ol>
      
      <h2>The Technology Stack</h2>
      <p>Stocks Protocol combines the best of two battle-tested blockchain networks:</p>
      <ul>
        <li><strong>Stellar:</strong> Provides fast consensus (3-5 seconds), low fees, and built-in compliance features</li>
        <li><strong>XRPL:</strong> Offers deep liquidity, cross-border payment rails, and institutional adoption</li>
      </ul>
      
      <p>This dual-chain approach ensures redundancy, maximizes liquidity, and gives users flexibility in how they interact with the protocol.</p>
      
      <h2>Getting Started</h2>
      <p>Ready to trade tokenized stocks? Create an account on Stocks Protocol and start trading in minutes. No minimum deposit required, and basic access is available globally.</p>
    `
  },
  'stellar-vs-xrpl': {
    title: 'Stellar vs XRPL: Why We Use Both',
    duration: '12 min',
    date: 'February 8, 2025',
    content: `
      <h2>The Dual-Chain Advantage</h2>
      <p>Most blockchain projects choose a single network. Stocks Protocol is different—we built on both Stellar and XRPL to combine their unique strengths. Here's why.</p>
      
      <h2>Understanding Stellar</h2>
      <p>Stellar (XLM) was designed specifically for financial applications. Founded in 2014 by Jed McCaleb (co-founder of Ripple), Stellar focuses on:</p>
      <ul>
        <li><strong>Speed:</strong> 3-5 second transaction finality</li>
        <li><strong>Cost:</strong> Average fee of 0.00001 XLM (~$0.000003)</li>
        <li><strong>Compliance:</strong> Built-in features for KYC/AML requirements</li>
        <li><strong>Asset Issuance:</strong> Native support for tokenized assets</li>
      </ul>
      
      <h3>Stellar's Consensus Protocol</h3>
      <p>Stellar uses the Stellar Consensus Protocol (SCP), a federated Byzantine agreement system. Unlike proof-of-work, SCP doesn't require mining—making it energy-efficient and fast.</p>
      
      <h2>Understanding XRPL</h2>
      <p>The XRP Ledger has processed billions in transactions since 2012. Key features include:</p>
      <ul>
        <li><strong>Liquidity:</strong> Deep order books and market maker networks</li>
        <li><strong>Decentralized Exchange:</strong> Built-in DEX functionality</li>
        <li><strong>Cross-Border Payments:</strong> Used by major financial institutions</li>
        <li><strong>Scalability:</strong> 1,500+ transactions per second</li>
      </ul>
      
      <h3>XRPL's Unique Features</h3>
      <p>XRPL pioneered the concept of a decentralized exchange built into the protocol layer. This means trading happens directly on-chain without needing separate smart contracts.</p>
      
      <h2>Why Both Networks?</h2>
      <p>By building on both Stellar and XRPL, Stocks Protocol achieves:</p>
      
      <h3>1. Maximum Liquidity</h3>
      <p>Users can access liquidity pools on both networks. If XRPL has better prices for NVIDIA tokens, your order routes there. If Stellar is cheaper for Apple, it goes there.</p>
      
      <h3>2. Redundancy & Reliability</h3>
      <p>If one network experiences congestion, the other continues operating. Your trades always go through.</p>
      
      <h3>3. User Choice</h3>
      <p>Some users prefer Stellar wallets, others prefer XRPL. Our dual-chain architecture supports both ecosystems.</p>
      
      <h3>4. Regulatory Flexibility</h3>
      <p>Different jurisdictions may prefer different networks. Our architecture adapts to regulatory requirements globally.</p>
      
      <h2>How Bridging Works</h2>
      <p>Moving assets between Stellar and XRPL is seamless:</p>
      <ol>
        <li>Initiate a bridge transaction from your wallet</li>
        <li>Tokens are locked on the source chain</li>
        <li>Equivalent tokens are minted on the destination chain</li>
        <li>Process completes in under 30 seconds</li>
      </ol>
      
      <h2>The Future</h2>
      <p>As both Stellar and XRPL continue to evolve, Stocks Protocol will integrate new features from both networks. Our dual-chain foundation positions us to adopt innovations from either ecosystem.</p>
    `
  },
  'how-tokenized-stocks-work': {
    title: 'How Tokenized Stocks Work',
    duration: '10 min',
    date: 'February 7, 2025',
    content: `
      <h2>What Are Tokenized Stocks?</h2>
      <p>Tokenized stocks are digital representations of real company shares, issued on a blockchain. Each token is backed 1:1 by actual stock held in regulated custody.</p>
      
      <h2>The Tokenization Process</h2>
      <p>Here's how real stocks become blockchain tokens:</p>
      
      <h3>Step 1: Acquisition</h3>
      <p>Stocks Protocol purchases real shares of companies like Apple, Tesla, or NVIDIA through licensed brokers.</p>
      
      <h3>Step 2: Custody</h3>
      <p>Shares are transferred to a regulated custodian—a financial institution that securely holds assets. Our custodians are licensed and insured.</p>
      
      <h3>Step 3: Token Minting</h3>
      <p>For each share in custody, one token is minted on the blockchain. If we custody 10,000 TSLA shares, exactly 10,000 sTSLA tokens exist.</p>
      
      <h3>Step 4: Distribution</h3>
      <p>Tokens are made available for trading on Stocks Protocol. Users can buy, sell, and transfer tokens freely.</p>
      
      <h2>Maintaining the 1:1 Backing</h2>
      <p>Trust in tokenized stocks requires proof of backing:</p>
      <ul>
        <li><strong>Real-Time Reserves:</strong> View our custody holdings anytime</li>
        <li><strong>Third-Party Audits:</strong> Monthly attestations from independent auditors</li>
        <li><strong>On-Chain Proof:</strong> Custody addresses are publicly verifiable</li>
      </ul>
      
      <h2>Token Economics</h2>
      <p>Tokenized stocks on Stocks Protocol maintain all economic benefits of real shares:</p>
      
      <h3>Dividends</h3>
      <p>When Apple pays a dividend, sTAPL holders receive their proportional share. Dividends are automatically distributed to token holders.</p>
      
      <h3>Stock Splits</h3>
      <p>If NVIDIA does a 10:1 split, your sNVDA tokens automatically multiply by 10. The protocol handles corporate actions seamlessly.</p>
      
      <h3>Voting Rights</h3>
      <p>Token holders can participate in shareholder votes through our governance portal. Your voice matters.</p>
      
      <h2>Trading Tokenized Stocks</h2>
      <p>Trading on Stocks Protocol works like any exchange:</p>
      <ol>
        <li>Connect your Stellar or XRPL wallet</li>
        <li>Deposit USDC, XLM, or XRP</li>
        <li>Place buy/sell orders for any tokenized stock</li>
        <li>Orders match instantly on our order book</li>
        <li>Tokens arrive in your wallet in 3-5 seconds</li>
      </ol>
      
      <h2>Redemption</h2>
      <p>Want real shares instead of tokens? Verified users can redeem tokens for actual stock delivery to their brokerage account. The process takes 3-5 business days.</p>
      
      <h2>Legal Framework</h2>
      <p>Tokenized stocks on Stocks Protocol are issued under regulatory frameworks that recognize digital securities. We work with legal experts across jurisdictions to ensure compliance.</p>
    `
  },
  'self-custody-guide': {
    title: 'Self-Custody Guide for Stock Tokens',
    duration: '7 min',
    date: 'February 6, 2025',
    content: `
      <h2>Your Keys, Your Stocks</h2>
      <p>One of the most powerful features of tokenized stocks is self-custody. Unlike traditional brokerages where your shares are held in "street name," blockchain tokens give you true ownership.</p>
      
      <h2>What is Self-Custody?</h2>
      <p>Self-custody means you control the private keys to your wallet. No intermediary can freeze, seize, or restrict access to your assets. You are your own bank.</p>
      
      <h2>Supported Wallets</h2>
      <p>Stocks Protocol tokens can be stored in any compatible wallet:</p>
      
      <h3>Stellar Wallets</h3>
      <ul>
        <li><strong>Lobstr:</strong> User-friendly mobile and web wallet</li>
        <li><strong>Solar:</strong> Desktop wallet with advanced features</li>
        <li><strong>Freighter:</strong> Browser extension for DeFi</li>
        <li><strong>Ledger:</strong> Hardware wallet support via Stellar app</li>
      </ul>
      
      <h3>XRPL Wallets</h3>
      <ul>
        <li><strong>XUMM:</strong> Most popular XRPL mobile wallet</li>
        <li><strong>Crossmark:</strong> Browser extension wallet</li>
        <li><strong>Ledger:</strong> Hardware wallet via XRP app</li>
        <li><strong>Trezor:</strong> Hardware wallet support</li>
      </ul>
      
      <h2>Setting Up Self-Custody</h2>
      
      <h3>Step 1: Choose Your Wallet</h3>
      <p>Select a wallet based on your needs. Mobile wallets offer convenience, hardware wallets offer maximum security.</p>
      
      <h3>Step 2: Secure Your Seed Phrase</h3>
      <p>When you create a wallet, you'll receive a 12-24 word seed phrase. This is your master key:</p>
      <ul>
        <li>Write it on paper (not digital)</li>
        <li>Store in multiple secure locations</li>
        <li>Never share with anyone</li>
        <li>Never enter it on websites</li>
      </ul>
      
      <h3>Step 3: Add Trust Lines</h3>
      <p>To hold tokenized stocks, your wallet needs trust lines to the issuing accounts. On Stellar and XRPL, this is a one-time setup per asset.</p>
      
      <h3>Step 4: Withdraw from Exchange</h3>
      <p>After purchasing tokens on Stocks Protocol, withdraw them to your self-custody wallet. Enter your wallet address and confirm the transaction.</p>
      
      <h2>Security Best Practices</h2>
      <ul>
        <li><strong>Use Hardware Wallets:</strong> For large holdings, hardware wallets are essential</li>
        <li><strong>Enable 2FA:</strong> Add extra security to exchange accounts</li>
        <li><strong>Verify Addresses:</strong> Always double-check wallet addresses before sending</li>
        <li><strong>Test Small First:</strong> Send a small amount before large transfers</li>
        <li><strong>Regular Backups:</strong> Ensure your seed phrase backup is current</li>
      </ul>
      
      <h2>Benefits of Self-Custody</h2>
      <ul>
        <li><strong>True Ownership:</strong> You own your tokens outright</li>
        <li><strong>No Counterparty Risk:</strong> Exchange hacks don't affect you</li>
        <li><strong>24/7 Access:</strong> Your tokens are always available</li>
        <li><strong>Privacy:</strong> Transactions without intermediaries</li>
        <li><strong>Global Portability:</strong> Access from anywhere in the world</li>
      </ul>
      
      <h2>When to Return to Exchange</h2>
      <p>Keep tokens on the exchange if you're actively trading. Move to self-custody for long-term holdings.</p>
    `
  },
  'understanding-settlement-times': {
    title: 'Understanding Settlement Times',
    duration: '5 min',
    date: 'February 5, 2025',
    content: `
      <h2>What is Settlement?</h2>
      <p>Settlement is when a trade becomes final—when ownership officially transfers from seller to buyer. It's one of the most important (yet overlooked) aspects of trading.</p>
      
      <h2>Traditional Settlement: T+2</h2>
      <p>In conventional stock markets, settlement takes T+2—two business days after the trade date:</p>
      <ul>
        <li><strong>Monday trade:</strong> Settles Wednesday</li>
        <li><strong>Thursday trade:</strong> Settles Monday (skips weekend)</li>
        <li><strong>Pre-holiday trade:</strong> Can take even longer</li>
      </ul>
      
      <h3>Why T+2 Exists</h3>
      <p>Traditional settlement involves multiple parties:</p>
      <ol>
        <li>Your broker</li>
        <li>The counterparty's broker</li>
        <li>A clearing house (like DTCC)</li>
        <li>Central securities depositories</li>
        <li>Custodian banks</li>
      </ol>
      <p>Coordinating all these entities takes time and creates delays.</p>
      
      <h2>Problems with Delayed Settlement</h2>
      
      <h3>Counterparty Risk</h3>
      <p>During the settlement window, either party could default. The 2008 financial crisis highlighted these risks.</p>
      
      <h3>Capital Inefficiency</h3>
      <p>Money is locked up waiting for settlement. You can't use proceeds from a sale for 2 days.</p>
      
      <h3>Failed Trades</h3>
      <p>Billions in trades fail to settle each year, requiring manual intervention and additional costs.</p>
      
      <h2>Blockchain Settlement: T+0</h2>
      <p>Stocks Protocol achieves T+0 settlement—trades are final in 3-5 seconds:</p>
      <ul>
        <li><strong>Instant Finality:</strong> No waiting period</li>
        <li><strong>Atomic Swaps:</strong> Tokens and payment exchange simultaneously</li>
        <li><strong>No Intermediaries:</strong> Direct peer-to-peer settlement</li>
        <li><strong>24/7 Operation:</strong> No business day limitations</li>
      </ul>
      
      <h2>How It Works</h2>
      <ol>
        <li>You submit a buy order for sTSLA</li>
        <li>Order matches with a seller on the order book</li>
        <li>Smart contract executes an atomic swap</li>
        <li>You receive sTSLA, seller receives USDC</li>
        <li>Transaction confirmed on blockchain in 3-5 seconds</li>
        <li>Settlement is final and irreversible</li>
      </ol>
      
      <h2>Real-World Impact</h2>
      
      <h3>For Traders</h3>
      <p>Instant settlement means you can immediately use proceeds from sales. Sell Apple, buy Tesla seconds later.</p>
      
      <h3>For Institutions</h3>
      <p>Reduced counterparty risk and capital requirements. No need to post collateral for pending settlements.</p>
      
      <h3>For the Market</h3>
      <p>Elimination of failed trades and settlement-related costs. A more efficient financial system.</p>
      
      <h2>The Future of Settlement</h2>
      <p>Major exchanges are exploring blockchain settlement. Stocks Protocol is leading this transformation today.</p>
    `
  },
  'getting-started': {
    title: 'Getting Started with Stocks Protocol',
    duration: '15 min',
    date: 'February 4, 2025',
    content: `
      <h2>Welcome to Stocks Protocol</h2>
      <p>This comprehensive guide will walk you through everything you need to start trading tokenized stocks on our platform.</p>
      
      <h2>Prerequisites</h2>
      <p>Before you begin, you'll need:</p>
      <ul>
        <li>A device with internet access (mobile or desktop)</li>
        <li>A valid email address</li>
        <li>Cryptocurrency for trading (USDC, XLM, or XRP)</li>
        <li>Basic understanding of wallets and transactions</li>
      </ul>
      
      <h2>Step 1: Create Your Account</h2>
      <ol>
        <li>Visit Stocks Protocol and click "Create Account"</li>
        <li>Enter your email and create a strong password</li>
        <li>Verify your email address</li>
        <li>Complete basic profile information</li>
      </ol>
      
      <h3>Verification Tiers</h3>
      <ul>
        <li><strong>Basic:</strong> Email verification only, limited trading</li>
        <li><strong>Standard:</strong> Identity verification, higher limits</li>
        <li><strong>Pro:</strong> Full verification, unlimited trading</li>
      </ul>
      
      <h2>Step 2: Set Up Your Wallet</h2>
      <p>You have two options for wallet setup:</p>
      
      <h3>Option A: Use Our Hosted Wallet</h3>
      <p>Perfect for beginners. We create and manage a wallet for you. Simple but you trust us with custody.</p>
      
      <h3>Option B: Connect External Wallet</h3>
      <p>For experienced users. Connect your own Stellar or XRPL wallet for full self-custody.</p>
      
      <h2>Step 3: Fund Your Account</h2>
      <p>Deposit funds to start trading:</p>
      
      <h3>Supported Currencies</h3>
      <ul>
        <li><strong>USDC:</strong> USD stablecoin (recommended)</li>
        <li><strong>XLM:</strong> Stellar Lumens</li>
        <li><strong>XRP:</strong> XRP for XRPL trading</li>
      </ul>
      
      <h3>Deposit Methods</h3>
      <ol>
        <li>Go to "Deposit" in your dashboard</li>
        <li>Select your currency</li>
        <li>Copy the deposit address</li>
        <li>Send from your external wallet or exchange</li>
        <li>Funds arrive in 1-5 minutes</li>
      </ol>
      
      <h2>Step 4: Explore the Markets</h2>
      <p>Browse available tokenized stocks:</p>
      <ul>
        <li><strong>Tech Giants:</strong> AAPL, MSFT, GOOGL, META</li>
        <li><strong>EV & Energy:</strong> TSLA, NVDA, RIVN</li>
        <li><strong>Finance:</strong> JPM, GS, V, MA</li>
        <li><strong>ETFs:</strong> SPY, QQQ, VTI</li>
      </ul>
      
      <h2>Step 5: Place Your First Trade</h2>
      
      <h3>Market Order</h3>
      <p>Buy/sell immediately at current market price:</p>
      <ol>
        <li>Select a stock (e.g., sAAPL)</li>
        <li>Click "Buy" or "Sell"</li>
        <li>Enter amount</li>
        <li>Review and confirm</li>
        <li>Order fills in seconds</li>
      </ol>
      
      <h3>Limit Order</h3>
      <p>Set your desired price:</p>
      <ol>
        <li>Select "Limit Order"</li>
        <li>Enter your target price</li>
        <li>Enter amount</li>
        <li>Submit order</li>
        <li>Order fills when price is reached</li>
      </ol>
      
      <h2>Step 6: Manage Your Portfolio</h2>
      <p>Track your holdings in the Portfolio section:</p>
      <ul>
        <li>View total portfolio value</li>
        <li>See individual position performance</li>
        <li>Track dividends received</li>
        <li>Download transaction history</li>
      </ul>
      
      <h2>Tips for New Traders</h2>
      <ul>
        <li><strong>Start Small:</strong> Begin with small amounts while learning</li>
        <li><strong>Diversify:</strong> Don't put everything in one stock</li>
        <li><strong>Use Limit Orders:</strong> Control your entry/exit prices</li>
        <li><strong>Enable Security:</strong> Set up 2FA immediately</li>
        <li><strong>Learn Self-Custody:</strong> Move long-term holdings to your own wallet</li>
      </ul>
      
      <h2>Getting Help</h2>
      <p>Need assistance? We're here to help:</p>
      <ul>
        <li><strong>FAQ:</strong> Common questions answered</li>
        <li><strong>Discord:</strong> Join our community</li>
        <li><strong>Support:</strong> Contact our team directly</li>
      </ul>
      
      <h2>Ready to Trade?</h2>
      <p>You now have everything you need to start trading tokenized stocks on Stocks Protocol. Welcome to the future of finance!</p>
    `
  }
};

const Article = () => {
  const { slug } = useParams();
  const article = articlesContent[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0b0014] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Article Not Found</h1>
          <Link to="/" className="text-purple-400 hover:text-purple-300">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0014]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0b0014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_stock-mirrors/artifacts/gdgmfuzc_2.%20Logo%202.png" 
                alt="Stocks Protocol" 
                className="h-40 w-auto object-contain"
              />
            </Link>
            
            <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft size={16} />
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="pt-28 pb-20">
        <article className="max-w-[900px] mx-auto px-6">
          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{article.duration} read</span>
              </div>
              <button className="flex items-center gap-2 hover:text-purple-400 transition-colors ml-auto">
                <Share2 size={16} />
                <span>Share</span>
              </button>
            </div>
          </header>

          {/* Article Body */}
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-semibold
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:text-gray-300 prose-ul:my-4
              prose-ol:text-gray-300 prose-ol:my-4
              prose-li:my-1
              prose-strong:text-white prose-strong:font-semibold
              prose-a:text-purple-400 prose-a:no-underline hover:prose-a:text-purple-300"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Back to Learn More */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <Link 
              to="/#learn-more" 
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Learn More
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
};

export default Article;
